import { useCallback } from 'react';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { useMap } from '@/providers/MapProvider';
import { fromLonLat } from 'ol/proj';

interface ZoomOptions {
  duration?: number;
  maxZoom?: number;
  padding?: number[];
}

export const useMapZoom = () => {
  const { map } = useMap();

  const zoomToFeature = useCallback(
    (feature: Feature, options: ZoomOptions = {}) => {
      if (!map) return;

      const {
        duration = 0,
        maxZoom = 18,
        padding = [100, 100, 100, 100],
      } = options;

      const geometry = feature.getGeometry();
      if (!geometry) return;

      if (geometry.getType() === 'Point') {
        // For points, zoom to center
        const coords = (geometry as Point).getCoordinates();
        map.getView().setCenter(coords);
        map.getView().setZoom(maxZoom);
      } else {
        const extent = geometry.getExtent();

        map.getView().fit(extent, {
          padding,
          maxZoom,
          duration,
        });
      }
    },
    [map],
  );

  const zoomToCoordinates = useCallback(
    (lon: number, lat: number, options: ZoomOptions = {}) => {
      if (!map) return;

      const { duration = 1000, maxZoom = 18 } = options;

      // Ensure we transform WGS84 to the map's current view projection
      const coords = fromLonLat([lon, lat], map.getView().getProjection());

      map.getView().animate({
        center: coords,
        zoom: maxZoom,
        duration,
      });
    },
    [map],
  );

  return {
    zoomToFeature,
    zoomToCoordinates,
  };
};
